import { riseClass, riseDelay, useReveal } from "../hooks/useReveal";

const reviews = [
  {
    name: "James H.",
    role: "Site Manager",
    rating: 5,
    text: "Focus Traffic Plans delivered our TMP within 24 hours, exactly what we needed to keep the project on track. The quality of the documentation was outstanding and got council sign-off first time.",
    initials: "JH",
  },
  {
    name: "Sarah N.",
    role: "Project Engineer",
    rating: 5,
    text: "We've used Focus Traffic Plans across multiple major road works projects. Their attention to detail and deep knowledge of RMS requirements makes them our go-to for every traffic guidance scheme.",
    initials: "SN",
  },
  {
    name: "Marcus W.",
    role: "Operations Director",
    rating: 5,
    text: "Incredibly responsive team. We had an urgent night works permit needed and they turned it around same day. That kind of reliability is rare and we won't be going anywhere else.",
    initials: "MW",
  },
  {
    name: "Priya S.",
    role: "Event Coordinator",
    rating: 5,
    text: "The event traffic management plan they produced for our large-scale outdoor event was thorough and professional. Authorities approved it without a single revision, first time that's happened for us.",
    initials: "PS",
  },
  {
    name: "Tom G.",
    role: "Construction Manager",
    rating: 5,
    text: "From vehicle movement plans to swept path analysis, Focus Traffic Plans handles everything. Their team genuinely understands the complexities of construction site access and it shows in every plan.",
    initials: "TG",
  },
  {
    name: "Lisa C.",
    role: "Principal Engineer",
    rating: 5,
    text: "We engaged Focus Traffic Plans for a complex freeway upgrade involving multiple lane closures and detour routes. The plans were clear, compliant, and delivered ahead of schedule.",
    initials: "LC",
  },
];

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          className={`h-4 w-4 ${i < count ? "text-orange-500" : "text-gray-200"}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function Avatar({ initials }) {
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-sm font-bold text-orange-600 ring-1 ring-orange-500/20">
      {initials}
    </div>
  );
}

export default function ReviewsSection() {
  const [sectionRef, started] = useReveal();

  return (
    <section ref={sectionRef} className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-14 text-center">
          <p className={`mb-3 text-xs font-bold uppercase tracking-[0.18em] text-orange-500 ${riseClass(started)}`}>
            Client reviews
          </p>
          <h2
            className={`text-3xl font-extrabold tracking-tight text-black sm:text-4xl ${riseClass(started)}`}
            style={riseDelay(started, 120)}
          >
            Trusted by industry professionals
          </h2>
          <p
            className={`mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-gray-500 ${riseClass(started)}`}
            style={riseDelay(started, 220)}
          >
            From small contractors to major government projects — here's what
            our clients say about working with us.
          </p>

          {/* Summary badge */}
          <div
            className={`mt-6 inline-flex items-center gap-3 rounded-full border border-orange-500/20 bg-orange-50 px-5 py-2.5 ${riseClass(started)}`}
            style={riseDelay(started, 340)}
          >
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} className="h-4 w-4 text-orange-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-800">
              5.0 <span className="font-normal text-gray-500">from 100+ verified clients</span>
            </span>
          </div>
        </div>

        {/* Review grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((review, index) => (
            <div
              key={review.name}
              className={riseClass(started)}
              style={riseDelay(started, 480 + index * 110)}
            >
            <div className="flex h-full flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition duration-300 hover:border-orange-500/30 hover:shadow-md hover:shadow-orange-500/5">
              <StarRating count={review.rating} />

              <p className="flex-1 text-[14px] leading-relaxed text-gray-600">
                &ldquo;{review.text}&rdquo;
              </p>

              <div className="flex items-center gap-3 border-t border-gray-100 pt-4">
                <Avatar initials={review.initials} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                  <p className="truncate text-[12px] text-gray-400">
                    {review.role}
                  </p>
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
